import React, { Component, memo } from 'react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Check } from 'lucide-react';
import { Step1, Step3 } from './components';
import { Company, Doc, DocTypes, User, UserType } from '@app/models';
import { FilePickerForm, FormDoc } from '@app/forms';
import { createDoc } from '@app/api';

interface Props {
  visible?: boolean;
  currentStep?: number;
  recipientUser?: Pick<User, 'id'> | null;
  recipientCompany?: Pick<Company, 'id'> | null;
  userType: UserType;
  companyId: string | null;
  onClose?: () => void;
  onSuccess?: (doc: Doc) => void;
}

interface State {
  company: Pick<Company, 'id'> | null;
  user: Pick<User, 'id'> | null;
  doc: Partial<Doc> | null;
  currentStep: number;
  maxCurrentStep: number;
  tenderId: string | null;
  pending: boolean;
  files: File[];
}

class ModalAddPartner extends Component<Props, State> {
  steps = [
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 0 ? 0 : currentStep,
        }));
      },
      title: 'Выберите партнера',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 1 ? 1 : currentStep,
        }));
      },
      title: 'Заполните данные договора',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 2 ? 2 : currentStep,
        }));
      },
      title: 'Выберите объявление (необязательно)',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 3 ? 3 : currentStep,
        }));
      },
      title: 'Загрузить документ',
    },
  ];

  state: State = {
    company: this.props.recipientCompany || null,
    user: this.props.recipientUser || null,
    doc: null,
    currentStep: this.props.currentStep || 0,
    maxCurrentStep: this.props.currentStep || 0,
    tenderId: null,
    pending: false,
    files: [],
  };

  onClickClose = () => {
    const { onClose } = this.props;
    if (!!onClose) {
      onClose();
    }
  };

  onSubmitCompany = (company: Company | null, user: User | null) => {
    this.setState(({ maxCurrentStep }) => ({
      company,
      user,
      currentStep: 1,
      maxCurrentStep: maxCurrentStep <= 1 ? 1 : maxCurrentStep,
    }));
  };

  onSubmitDoc = (doc: Partial<Doc>) => {
    this.setState(({ maxCurrentStep }) => ({
      doc,
      currentStep: 2,
      maxCurrentStep: maxCurrentStep <= 2 ? 2 : maxCurrentStep,
    }));
  };

  onSubmitTender = (tenderId: string | null) => {
    this.setState(({ maxCurrentStep }) => ({
      tenderId,
      currentStep: 3,
      maxCurrentStep: maxCurrentStep <= 3 ? 3 : maxCurrentStep,
    }));
  };

  onSubmitFiles = (files: File[]) => {
    this.setState(
      {
        files,
        pending: true,
      },
      async () => {
        await this.createPartner();
      }
    );
  };

  createPartner = async () => {
    try {
      const { userType, companyId, onSuccess } = this.props;
      const { company, user, tenderId, files, doc } = this.state;
      const params: Partial<Doc> = {
        ...doc,
      };
      if (!!company) {
        params.recipientCompanyId = company.id;
      }
      if (!!user) {
        params.recipientId = user.id;
      }
      if (userType === UserType.ENTITY) {
        params.companyId = companyId!;
      }
      if (!!tenderId) {
        params.tenderId = tenderId;
      }
      const docResponse = await createDoc(params, files);
      this.setState(
        {
          company: null,
          doc: null,
          currentStep: 0,
          maxCurrentStep: 0,
          tenderId: null,
          pending: false,
          files: [],
        },
        () => {
          onSuccess!(docResponse);
        }
      );
    } catch (e) {
      this.setState({
        pending: false,
      });
    }
  };

  renderStepper = () => {
    const { currentStep, maxCurrentStep } = this.state;

    return (
      <Card className="mb-4">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              Заключить договор с новым партнером
            </h2>
          </div>

          <div className="block sm:hidden">
            <div className="space-y-2">
              {this.steps.map((step, index) => {
                const isCompleted = index < currentStep;
                const isAccessible = index <= maxCurrentStep;

                return (
                  <div key={index} className="flex items-center space-x-3">
                    <div
                      className={`
                        flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                        ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : isAccessible
                            ? 'bg-white border border-gray-300 text-black'
                            : 'bg-gray-100 text-gray-400'
                        }
                      `}
                    >
                      {isCompleted ? <Check className="w-3 h-3" /> : index + 1}
                    </div>
                    <div className="flex-1">
                      <button
                        onClick={isAccessible ? step.onClick : undefined}
                        disabled={!isAccessible}
                        className={`
                          text-left text-sm font-medium border-none outline-none
                          ${
                            isAccessible
                              ? 'text-gray-800 cursor-pointer'
                              : 'text-gray-400 cursor-default'
                          }
                        `}
                        style={{
                          background: 'transparent',
                          backgroundColor: 'transparent',
                          color: isAccessible ? '#1f2937' : '#9ca3af',
                          border: 'none',
                          padding: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500',
                        }}
                      >
                        {step.title}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden sm:block">
            <div className="flex items-center justify-between">
              {this.steps.map((step, index) => {
                const isCompleted = index < currentStep;
                const isAccessible = index <= maxCurrentStep;

                return (
                  <div key={index} className="flex items-center flex-1">
                    <div className="flex items-center">
                      <div
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                          ${
                            isCompleted
                              ? 'bg-green-500 text-white'
                              : isAccessible
                              ? 'bg-white border border-gray-300 text-black'
                              : 'bg-gray-100 text-gray-400'
                          }
                        `}
                      >
                        {isCompleted ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="ml-3">
                        <button
                          onClick={isAccessible ? step.onClick : undefined}
                          disabled={!isAccessible}
                          className={`
                            text-sm font-medium border-none outline-none
                            ${
                              isAccessible
                                ? 'text-gray-800 hover:text-gray-900 cursor-pointer'
                                : 'text-gray-400 cursor-default'
                            }
                          `}
                          style={{
                            background: 'transparent',
                            backgroundColor: 'transparent',
                            color: isAccessible ? '#1f2937' : '#9ca3af',
                            border: 'none',
                            padding: 0,
                            fontSize: '0.875rem',
                            fontWeight: '500',
                          }}
                        >
                          {step.title}
                        </button>
                      </div>
                    </div>
                    {index < this.steps.length - 1 && (
                      <div className="flex-1 mx-4">
                        <div
                          className={`
                            h-0.5 
                            ${
                              index < currentStep
                                ? 'bg-green-500'
                                : 'bg-gray-200'
                            }
                          `}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  render() {
    const { visible } = this.props;
    const { currentStep, pending } = this.state;

    if (!visible) {
      return null;
    }

    return (
      <Dialog open={visible} onOpenChange={() => this.onClickClose()}>
        <DialogContent className="max-w-6xl p-0 max-h-[90vh] flex flex-col">
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="p-4 sm:p-6 pb-0 flex-shrink-0">
              {this.renderStepper()}
            </div>

            <div className="p-4 sm:p-6 pt-0 flex-1 overflow-y-auto">
              {currentStep === 0 && (
                <div className="min-h-[300px]">
                  <Step1 onSubmit={this.onSubmitCompany} />
                </div>
              )}

              {currentStep === 1 && (
                <div className="min-h-[400px] sm:min-h-[500px]">
                  <FormDoc
                    onSubmit={this.onSubmitDoc}
                    type={DocTypes.CONTRACT}
                  />
                </div>
              )}

              {currentStep === 2 && (
                <div className="min-h-[300px]">
                  <Step3 onSubmit={this.onSubmitTender} />
                </div>
              )}

              {currentStep === 3 && (
                <div className="min-h-[300px]">
                  <FilePickerForm
                    accept="application/pdf"
                    pending={pending}
                    defaultExpanded={currentStep === 3}
                    onSubmit={this.onSubmitFiles}
                    required={true}
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

export default memo(ModalAddPartner);
